/**
 * Web → native bridge for the iOS companion shell (see ios/). A NO-OP in a
 * normal browser, so it costs nothing until the native host is present.
 *
 * The web hero-selection rule (homeHero.ts, plan step 5) stays the single
 * source of truth: it computes the "Right Now" winner and calls postGlance()
 * (and stashPass() for a QR), and the native shell renders the Live Activity /
 * Dynamic Island / Lock Screen widget from it — nothing is decided natively.
 *
 * ADR-014: `line1`/`line2` MUST already be name-scrubbed (flight/venue CODES +
 * times only — never a client, gig or venue name) before they cross. The QR
 * bytes never ride a message; only an opaque `qrRef` does.
 */
export interface TDGlance {
  state: "credential" | "leave_now" | "next" | "gap_risk";
  /** epoch ms of the governing moment; native ticks the countdown from this. */
  primaryTime: number;
  line1: string; // "AF1234 · CDG→JFK"
  line2: string; // "14:05"
  accent: "transit" | "venue" | "lodging" | "dayoff";
  qrRef?: string; // opaque handle; the raster is stashed via stashPass()
  gapSlackMin?: number;
  gapPlace?: string;
}

interface NativeBridge {
  postMessage(msg: unknown): void;
}

function bridge(): NativeBridge | null {
  const w = window as unknown as { webkit?: { messageHandlers?: { td?: NativeBridge } } };
  return w.webkit?.messageHandlers?.td ?? null;
}

/** True only inside the native iOS shell — gate any native-only UI on this. */
export function isNativeHost(): boolean {
  return bridge() !== null;
}

/** Push the current hero glance to native (no-op in a browser). */
export function postGlance(glance: TDGlance): void {
  bridge()?.postMessage({ kind: "glance", payload: glance });
}

/**
 * Stash a QR raster (a data URL) in the native App Group container under `ref`,
 * so the widget/activity can render it without the bytes ever crossing a push
 * (ADR-014). No-op in a browser.
 */
export function stashPass(ref: string, dataURL: string): void {
  bridge()?.postMessage({ kind: "pass", ref, dataURL });
}
